/**
 * Regular expression for matching quarter formats like "FY22 Q1", "Q1 FY22", "2022 Q1", "Q1 2022"
 */
export const regexQuarters =
  /\b(FY\d{2} Q[1-4]|Q[1-4] FY\d{2}|\d{4} Q[1-4]|Q[1-4] \d{4})\b/;

/**
 * Determine the appropriate label key based on chart type and sort axis
 * @param {string} chartType - The type of chart
 * @param {string} sortAxis - The sort axis ('calc_asc', 'calc_desc', or other)
 * @returns {string} The appropriate label key
 */
export function getLabelKey(chartType, sortAxis) {
  if (chartType === 'PieChart') {
    return 'value';
  }

  return sortAxis === 'calc_asc' || sortAxis === 'calc_desc'
    ? 'display_value'
    : 'row_label';
}

/**
 * Sum all display values in an object
 * @param {Object} obj - The object containing display values
 * @returns {number} The sum of all display values
 */
export function sumDisplayValues(obj) {
  return Object.keys(obj)
    .filter(key => key.includes('display_value'))
    .reduce((sum, key) => {
      return sum + (Number(obj[key]) || 0);
    }, 0);
}

/**
 * Determine the data type for an axis based on data and metadata
 * @param {Array} data - The chart data
 * @param {string} chartType - The type of chart
 * @param {Object} meta - The chart metadata
 * @param {string} sortAxis - The sort axis (optional)
 * @returns {string} The detected data type ('number', 'date', 'string', or 'status')
 */
export function determineDataTypeForAxis(data, chartType, meta, sortAxis) {
  const labelKey = getLabelKey(chartType, sortAxis);
  const sortableData = data;

  const firstValidItem = sortableData.find(item => Boolean(item[labelKey]));

  let dataType = firstValidItem
    ? detectDataType(firstValidItem[labelKey])
    : 'string';

  // Check if it's a status field
  if (
    meta?.reportColumnNames?.label?.toLowerCase()?.includes('status') &&
    (!sortAxis || !sortAxis?.includes('calc'))
  ) {
    dataType = 'status';
  }

  // Force number type for calculation-based sorting
  if (sortAxis?.includes('calc')) {
    dataType = 'number';
  }

  return dataType;
}

/**
 * Sort data based on data type and sort direction
 * @param {Array} data - The data to sort
 * @param {string} chartType - The type of chart
 * @param {string} sortAxis - The sort axis ('asc', 'desc', 'calc_asc', 'calc_desc', 'none')
 * @param {string} dataType - The data type ('number', 'date', 'string', 'status')
 * @returns {Array} The sorted data
 */
export function sortDataByType(data, chartType, sortAxis, dataType) {
  if (!sortAxis || sortAxis === 'none' || chartType === 'ScatterPlot') {
    return data;
  }

  const labelKey = getLabelKey(chartType, sortAxis);

  return [...data].sort((a, b) => {
    let aValue = a[labelKey];
    let bValue = b[labelKey];

    if (sortAxis === 'calc_desc' || sortAxis === 'calc_asc') {
      aValue = sumDisplayValues(a);
      bValue = sumDisplayValues(b);
    }

    switch (dataType) {
      case 'number':
        return sortAxis === 'desc' || sortAxis === 'calc_desc'
          ? Number(bValue) - Number(aValue)
          : Number(aValue) - Number(bValue);
      case 'date': {
        let aDate = new Date(aValue).getTime();
        let bDate = new Date(bValue).getTime();
        if (regexQuarters.test(aValue)) {
          aDate = parseFiscalQuarterToDate(aValue).getTime();
        }
        if (regexQuarters.test(bValue)) {
          bDate = parseFiscalQuarterToDate(bValue).getTime();
        }
        return sortAxis === 'desc' ? bDate - aDate : aDate - bDate;
      }
      case 'status':
        return sortAxis === 'desc' ? -1 : 1;
      default: // string
        if (
          (aValue === null || aValue === undefined) &&
          (bValue === null || bValue === undefined)
        ) {
          return 0;
        }
        if (aValue === null || aValue === undefined) {
          return 1;
        }
        if (bValue === null || bValue === undefined) {
          return -1;
        }

        if (sortAxis === 'desc') {
          return bValue.localeCompare(aValue);
        }
        return aValue.localeCompare(bValue);
    }
  });
}

/**
 * Detect the data type of a value (number, date, string, or status)
 * @param {*} value - The value to check
 * @returns {'number'|'date'|'string'|'status'} The detected data type
 */
export function detectDataType(value) {
  // Check if it's a valid number (including decimals)
  if (!isNaN(Number(value)) && value !== '') {
    return 'number';
  }

  // Convert value to string for regex testing
  const valueStr = String(value);

  // Check if it's a valid date
  const date = new Date(value);
  if (
    (date instanceof Date && !isNaN(date.getTime())) ||
    regexQuarters.test(valueStr)
  ) {
    return 'date';
  }

  // Default to string
  return 'string';
}

/**
 * Parse a fiscal quarter string (like "FY22 Q1" or "Q1 2022") into a Date object
 * @param {string} quarterStr - The quarter string to parse
 * @returns {Date} The date representing the start of the quarter
 */
export function parseFiscalQuarterToDate(quarterStr) {
  const match = quarterStr.match(/FY(\d{2})|(\d{4})|Q([1-4])/g);
  if (!match || match.length < 2) throw new Error('Invalid quarter format');

  const year = match[0].startsWith('FY')
    ? 2000 + parseInt(match[0].slice(2), 10)
    : parseInt(match[0], 10);

  // Find the quarter part (Q1, Q2, Q3, Q4)
  const quarterPart = match.find(m => m.startsWith('Q'));
  if (!quarterPart) throw new Error('Invalid quarter format: missing Q part');

  const quarter = parseInt(quarterPart.slice(1), 10);

  return new Date(year, (quarter - 1) * 3, 1);
}

/**
 * Get sort options based on data type and chart type
 * @param {string} type - The data type ('date', 'string', 'number', 'status')
 * @param {string} chartType - The chart type
 * @returns {Array<Array<string|string>>} Array of sort options [label, value]
 */
export function getSortOptionsForType(type, chartType) {
  const defaultOption = ['Default', 'none'];

  let sortOptions;

  switch (type) {
    case 'date':
      sortOptions = [
        defaultOption,
        ['Axis values Newest to oldest', 'desc'],
        ['Axis values Oldest to newest', 'asc'],
      ];
      break;
    case 'string':
      sortOptions = [
        defaultOption,
        ['Axis values A to Z', 'asc'],
        ['Axis values Z to A', 'desc'],
      ];
      break;
    case 'number':
      sortOptions = [
        defaultOption,
        ['Axis values 1 to 9', 'asc'],
        ['Axis values 9 to 1', 'desc'],
      ];
      break;
    case 'status':
      sortOptions = [
        defaultOption,
        ['Axis values First to last', 'asc'],
        ['Axis values Last to first', 'desc'],
      ];
      break;
    default:
      sortOptions = [defaultOption];
  }

  if (!['ScatterBubblePlot', 'ScatterPlot'].includes(chartType)) {
    let dataValueLabel = 'Bar size';
    switch (chartType) {
      case 'LineChart':
        dataValueLabel = 'Line values';
    }

    sortOptions.push([`${dataValueLabel} 1 to 9`, 'calc_asc']);
    sortOptions.push([`${dataValueLabel} 9 to 1`, 'calc_desc']);
  }

  return sortOptions;
}

/**
 * Provide a display format large numbers with commas (e.g., 123,456)
 * @param  {number | string} value - value to be displayed
 */
export function formatDisplayValue(value) {
  // Javascript only supports 16 decimal places, so allow fractional numbers
  // to present in scientific notation
  if (typeof value === 'number' && value >= 1) {
    return value.toLocaleString('en');
  }
  return value;
}
